import { useCallback, useEffect, useRef, useState } from "react";
import type { User } from "@/features/auth/model/authTypes";
import { toastService } from "@/shared/toast";
import {
  getProfile,
  updateProfile,
  updateProfileAvatar,
  type UpdateProfileFieldsInput,
} from "@/features/settings/services/profile.adapter";

type ProfileForm = {
  firstName: string;
  lastName: string;
  phone: string;
  address: string;
  avatarUrl: string;
  email: string;
};

type ProfileUIState = {
  loading: boolean;
  uploadingAvatar: boolean;
  errorMessage: string | null;
  firstNameError: string | null;
};

const FIRST_NAME_REQUIRED_ERROR = "First name is required.";

const DEFAULT_PROFILE_IMAGE_URL = "";

const toForm = (user: User): ProfileForm => {
  return {
    firstName: user.first_name ?? "",
    lastName: user.last_name ?? "",
    phone: user.phone ?? "",
    address: user.mailing_address ?? "",
    avatarUrl: user.avatar ?? DEFAULT_PROFILE_IMAGE_URL,
    email: user.email ?? "",
  };
};

const toUpdatePayload = (form: ProfileForm): UpdateProfileFieldsInput => {
  return {
    first_name: form.firstName.trim(),
    last_name: form.lastName.trim(),
    phone: form.phone.trim() ? form.phone.trim() : null,
    mailing_address: form.address.trim() ? form.address.trim() : null,
  };
};

const initialProfile: ProfileForm = {
  firstName: "",
  lastName: "",
  phone: "",
  address: "",
  avatarUrl: DEFAULT_PROFILE_IMAGE_URL,
  email: "",
};

export const useSettingsProfileViewModel = () => {
  const [profile, setProfile] = useState<ProfileForm>(initialProfile);
  const initialProfileRef = useRef<ProfileForm>(initialProfile);
  const [isEdit, setIsEdit] = useState(false);
  const [ui, setUI] = useState<ProfileUIState>({
    loading: false,
    uploadingAvatar: false,
    errorMessage: null,
    firstNameError: null,
  });

  const onFirstNameChange = (firstName: string) => {
    setProfile((s) => ({ ...s, firstName }));
    if (firstName.trim()) {
      setUI((s) => ({ ...s, firstNameError: null }));
    }
  };
  const onLastNameChange = (lastName: string) => setProfile((s) => ({ ...s, lastName }));
  const onPhoneChange = (phone: string) => setProfile((s) => ({ ...s, phone }));
  const onAddressChange = (address: string) => setProfile((s) => ({ ...s, address }));

  const getUserProfile = useCallback(async () => {
    setUI((s) => ({ ...s, loading: true, errorMessage: null }));
    const result = await getProfile();
    if (result.status === "success") {
      const form = toForm(result.data);
      initialProfileRef.current = form;
      setProfile(form);
    } else {
      setUI((s) => ({ ...s, errorMessage: result.message }));
    }
    setUI((s) => ({ ...s, loading: false }));
  }, []);

  useEffect(() => {
    void getUserProfile();
  }, [getUserProfile]);

  const startEdit = () => {
    setIsEdit(true);
    setUI((s) => ({ ...s, firstNameError: null, errorMessage: null }));
  };

  const cancelEdit = () => {
    setProfile(initialProfileRef.current);
    setIsEdit(false);
    setUI((s) => ({ ...s, errorMessage: null, firstNameError: null }));
  };

  const saveProfile = useCallback(async () => {
    if (!profile.firstName.trim()) {
      setUI((s) => ({ ...s, firstNameError: FIRST_NAME_REQUIRED_ERROR }));
      toastService.show(FIRST_NAME_REQUIRED_ERROR, "error");
      return;
    }

    setUI((s) => ({ ...s, loading: true, errorMessage: null, firstNameError: null }));
    const payload = toUpdatePayload(profile);
    const result = await updateProfile(payload);
    if (result.status === "success") {
      const form = toForm(result.data);
      initialProfileRef.current = form;
      setProfile(form);
      setIsEdit(false);
      toastService.show("Profile updated successfully", "success");
    } else {
      setUI((s) => ({ ...s, errorMessage: result.message }));
      toastService.show(result.message, "error");
    }
    setUI((s) => ({ ...s, loading: false }));
  }, [profile]);

  const uploadAvatar = useCallback(async (file: File) => {
    setUI((s) => ({ ...s, uploadingAvatar: true, errorMessage: null }));

    // Optimistic preview
    const previewUrl = URL.createObjectURL(file);
    setProfile((s) => ({ ...s, avatarUrl: previewUrl }));

    const formData = new FormData();
    formData.append("file", file);

    const result = await updateProfileAvatar(formData);
    if (result.status === "success") {
      const form = toForm(result.data);
      initialProfileRef.current = form;
      setProfile(form);
      toastService.show("Avatar updated successfully", "success");
    } else {
      setUI((s) => ({ ...s, errorMessage: result.message }));
      toastService.show(result.message, "error");
      setProfile(initialProfileRef.current);
    }

    URL.revokeObjectURL(previewUrl);
    setUI((s) => ({ ...s, uploadingAvatar: false }));
  }, []);

  return {
    form: {
      profile,
      isEdit,
    },
    ui,
    actions: {
      startEdit,
      cancelEdit,
      onFirstNameChange,
      onLastNameChange,
      onPhoneChange,
      onAddressChange,
      getUserProfile,
      saveProfile,
      uploadAvatar,
    },
  };
};
